<template>
  <div class="laboratory-item-input-form">
    <div class="item name" :style="itemNameStyles" data-test="item name">
      <div class="text" data-test="text name">
        <span>{{ getEnName(laboratoryItem) }}</span>
      </div>
    </div>
    <div class="item unit">
      <validation-provider
        v-if="hasUnitAndRange"
        class="validation-provider"
        :rules="{ notSurrogatePair: true }"
        v-slot="{ errors }"
        data-test="validation-provider unit"
      >
        <base-text-box
          data-test="base-text-box unit"
          :maxlength="10"
          :styles="{ width: '140px' }"
          :value="laboratoryItem.unit"
          @input="inputLaboratoryItemValue(index, 'unit', $event)"
        />
        <div class="error" data-test="error unit">
          <span>{{ errors[0] }}</span>
        </div>
      </validation-provider>
    </div>
    <div
      v-if="isInstrumentWithMonthAgeDependentItem"
      class="item age"
      data-test="item age"
    >
      <div v-for="age in monthAges" :key="age.id + '-age'" class="age-item">
        <div class="min" data-test="min">{{ age.minMonthAge }}</div>
        <div class="line">–</div>
        <div class="max" data-test="max">{{ age.maxMonthAge }}</div>
      </div>
    </div>
    <div class="item ranges">
      <div
        v-for="laboratoryReferenceRange in laboratoryReferenceRangesByLaboratoryItemCode"
        :key="laboratoryReferenceRange.id + '-range'"
        class="range-item"
      >
        <laboratory-reference-range-input-form
          :laboratoryReferenceRange="laboratoryReferenceRange"
          @input-value="$emit('input-laboratory-reference-range-value', $event)"
          data-test="laboratory-reference-range-input-form"
        />
      </div>
    </div>
    <div class="item description">
      <validation-provider
        class="validation-provider"
        :rules="{ notSurrogatePair: true }"
        v-slot="{ errors }"
      >
        <base-multiple-lines-text-box
          :maxlength="200"
          :placeholder="'説明文'"
          :value="laboratoryItem.description"
          :rootStyles="{ height: '100%' }"
          :styles="{ height: '100%', minHeight: '87px' }"
          @input="inputLaboratoryItemValue(index, 'description', $event)"
          data-test="base-multiple-lines-text-box description"
        />
        <div class="error" data-test="err">{{ errors[0] }}</div>
      </validation-provider>
    </div>
  </div>
</template>

<script>
import BaseTextBox from '@/components/parts/atoms/BaseTextBox'
import BaseMultipleLinesTextBox from '@/components/parts/atoms/BaseMultipleLinesTextBox'
import LaboratoryReferenceRangeInputForm from '@/components/parts/molecules/LaboratoryReferenceRangeInputForm'
import { ValidationProvider } from 'vee-validate'
import { mapGetters } from 'vuex'
import { hasUnitAndRange } from '@/utils/measurement_utils'

export default {
  name: 'LaboratoryItemInputForm',

  components: {
    ValidationProvider,
    BaseTextBox,
    BaseMultipleLinesTextBox,
    LaboratoryReferenceRangeInputForm
  },

  props: {
    index: { type: Number, default: 0 },
    laboratoryItem: { type: Object, default: () => {} },
    laboratoryReferenceRangesByLaboratoryItemCode: {
      type: Array,
      default: () => []
    },
    isInstrumentWithMonthAgeDependentItem: { type: Boolean, default: false },
    itemNameStyles: { type: Object }
  },

  computed: {
    ...mapGetters({
      getMasterLaboratoryItem: 'master/getMasterLaboratoryItemIndexedByCode',
      getLaboratoryItems: 'laboratoryItems/getData',
      getLaboratoryReferenceRanges: 'laboratoryReferenceRanges/getData'
    }),
    hasUnitAndRange() {
      return hasUnitAndRange(this.laboratoryItem.masterLaboratoryItemCode)
    },
    monthAges() {
      return this.laboratoryReferenceRangesByLaboratoryItemCode
        .filter(v => {
          if (v.minMonthAge === -1 && v.maxMonthAge === -1) {
            // 月齢によって基準値を分けていないデータの場合は除外する
            return false
          } else {
            // イヌとネコの月齢の値は全く同じなので、画面での月齢情報の表示のため片方のデータだけに絞る
            return v.masterLaboratorySpeciesCode === '1'
          }
        })
        .map(v => {
          if (v.minMonthAge === -1) {
            return { ...v, minMonthAge: 0 }
          } else if (v.maxMonthAge === -1) {
            return { ...v, maxMonthAge: '' }
          } else {
            return v
          }
        })
    }
  },

  methods: {
    getEnName(laboratoryItem) {
      const masterLaboratoryItem = this.getMasterLaboratoryItem(
        laboratoryItem.masterLaboratoryItemCode
      )
      return masterLaboratoryItem.enName
    },
    inputLaboratoryItemValue(index, key, value) {
      this.$emit('input-laboratory-item-value', { index, key, value })
    }
  }
}
</script>

<style lang="scss" scoped>
.laboratory-item-input-form {
  margin: 18px 0;
  display: flex;
  border-bottom: 2px solid #{$light-grey};
  padding-bottom: 18px;
  > .item {
    margin-left: 20px;
    text-align: center;
    &.name {
      margin-left: 0px;
      justify-content: left;
      text-align: left;
      font-size: 15px;
      line-height: 33px;
    }
    &.unit {
      width: 140px;
    }
    > .validation-provider {
      position: relative;
      height: 100%;
      > .error {
        position: absolute;
        z-index: 10;
        color: #{$tomato};
        font-size: 11px;
        text-align: left;
        white-space: nowrap;
        background-color: #{$white};
      }
    }
    &.age {
      width: 120px;
      text-align: center;
      > .age-item {
        height: 33px;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 16px;
        & ~ .age-item {
          margin-top: 18px;
        }
        > .min {
          width: 40px;
          text-align: center;
        }
        > .line {
          width: 20px;
          text-align: center;
        }
        > .max {
          width: 40px;
          text-align: center;
        }
      }
    }
    &.ranges {
      margin-left: 0px;
      display: flex;
      flex-wrap: wrap;
      width: 446px;
      .range-item:nth-of-type(3),
      .range-item:nth-of-type(4),
      .range-item:nth-of-type(5),
      .range-item:nth-of-type(6) {
        margin-top: 18px;
      }
    }
  }
}
</style>
